import { t } from "i18next";
import { useContext } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Tooltip } from "antd";
import { Table } from "../../../../../components/Table/Table";
import {
  renderOznakaSaKolicinomSerijskiBrojevi,
  renderOznakeSaKolicinom,
  renderOznakeSaKolicinomPoAmbalazi,
} from "../../../../../modules/obligee/periodicReports/periodicReport.functions";
import { PeriodicReportsContext } from "../../../../../modules/obligee/periodicReports/periodicReports.context";
import { AlcoholReport } from "../../../../../modules/obligee/periodicReports/periodicReports.types";
import { useScreenResize } from "../../../../../utils/useScreenResize";

export const AlcoholTable = () => {
  const { periodicReports } = useContext(PeriodicReportsContext);
  const { width } = useScreenResize();

  const colDefAlcohol = [
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_I_DATUM_ZAMA"),
      children: [
        {
          title: "1",
          width: "240px",
          fixed: "left",
          dataIndex: "brojIDatumZahteva",
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.SLOVNA_OZNAKA_I_KOLICINA"),
      children: [
        {
          title: "2",
          width: "240px",
          fixed: "left",
          dataIndex: "slovnaOznakaIKolicinaPreuzetihMarkica",
          render: (_: string, record: AlcoholReport) => (
            <Flex gap={10} vertical>
              {record.slovnaOznakaIKolicinaPreuzetihMarkica?.map((oznaka) => {
                return oznaka ? <span>{oznaka}</span> : <br />;
              })}
            </Flex>
          ),
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.OZNAKA_SERIJE_I_SERIJSKI_BROJ"),
      children: [
        {
          title: "3",
          fixed: "left",
          width: "240px",
          dataIndex: "oznakaSerijeISerijskiBrojPreuzetihMarkica",
          render: (_: string, record: AlcoholReport) => (
            <Flex gap={10} vertical>
              {record.oznakaSerijeISerijskiBrojPreuzetihMarkica?.map(
                (oznaka) => {
                  return oznaka ? <span>{oznaka}</span> : <br />;
                }
              )}
            </Flex>
          ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_4")}>
          {t("PERIODICNI_IZVESTAJI.STANJE_NERAZDUZENIH_MARKICA")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "4",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakeSaKolicinom(
              record.brojNeutrosenihMarkicaPrethodniKvartal
            ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_5")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_FLASA")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "5",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakeSaKolicinom(
              record.brojIskoriscenihMarkicaIzProizvodnje
            ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_6")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_PROMET")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "6",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakeSaKolicinom(record.brojIskoriscenihMarkicaIzUvoza),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_ALKOHOL_7")}>
          {t("PERIODICNI_IZVESTAJI.ZAPREMINA_FLASE")} <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "7",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakeSaKolicinomPoAmbalazi(record.zapreminaAmbalaze),
        },
      ],
    },
    {
      width: "240px",
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_8")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      children: [
        {
          title: "8",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakaSaKolicinomSerijskiBrojevi(
              record.brojOstecenihVidljivihMarkica
            ),
        },
      ],
    },
    {
      width: "240px",
      title: t(
        "PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_NEVIDLJIVI"
      ),
      children: [
        {
          title: "9",
          width: "240px",
          render: (record: AlcoholReport) =>
            renderOznakeSaKolicinom(
              record.brojOstecenihNevidljivihMarkica,
              true
            ),
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE"),
      width: "240px",
      children: [
        {
          title: "10",
          width: "260px",
          render: (record: AlcoholReport) =>
            renderOznakaSaKolicinomSerijskiBrojevi(
              record.brojNerazduzenihMarkicaISerijskiBrojNaKraju
            ),
        },
      ],
    },
  ];

  const scrollConfig =
    width < 992
      ? undefined
      : [".ant-tabs-nav", "#periodic", "#alcohol_table_header"];

  return (
    <div>
      <div id="alcohol_table_header" style={{ display: "flex" }}>
        <h2 className="alignCenter">
          {t("AKCIZNE_MARKICE.ALKOHOL").toUpperCase()}
        </h2>
      </div>
      <Table
        columns={colDefAlcohol}
        scrollConfig={scrollConfig}
        dataSource={periodicReports.alcoholReport}
        name="periodicni-izvestaji-tabela-alkohol"
      />
    </div>
  );
};
